import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { notifications } from "@mantine/notifications";
import { bridgeService, type Device } from "../services/bridgeService";
import {
  workstationPreferenceService,
  type BridgeDeviceType,
} from "../services/workstationPreference.service";
import { slideLabelPrinterService } from "../services/slideLabelPrinter.service";
import { cassetteLabelPrinterService } from "../services/cassetteLabelPrinter.service";
import { documentScanningService } from "../services/documentScanning.service";

export interface WorkstationDeviceStatus {
  /** Whether a workstation is selected in preferences */
  hasWorkstation: boolean;
  /** Whether the selected workstation is currently online */
  workstationOnline: boolean;
  /** The resolved device for the requested type */
  device: Device | null;
  /** Whether the specific device is connected */
  deviceConnected: boolean;
  /** Loading state */
  isLoading: boolean;
  /** The workstation name */
  workstationName: string | null;
}

/**
 * Hook that checks the real connection status of a device type
 * by querying the backend for the selected workstation + device.
 *
 * Usage:
 *   const status = useWorkstationDeviceStatus("slide_printer");
 */
export function useWorkstationDeviceStatus(
  deviceType: BridgeDeviceType
): WorkstationDeviceStatus {
  const [workstationId, setWorkstationId] = useState<string | null>(
    workstationPreferenceService.getSelectedWorkstationId()
  );

  // Listen for preference changes
  useEffect(() => {
    const unsub = workstationPreferenceService.onChange(() => {
      setWorkstationId(workstationPreferenceService.getSelectedWorkstationId());
    });
    return unsub;
  }, []);

  const {
    data: workstation,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["bridge-workstation", workstationId],
    queryFn: () => bridgeService.getWorkstation(workstationId!),
    select: (res) => res.data,
    enabled: !!workstationId,
    refetchInterval: 30000, // poll every 30s
    staleTime: 10000,
    retry: (failureCount, err) => {
      if (err instanceof AxiosError && err.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const wsOnline = workstation?.status === "online";
  const deviceId = workstationPreferenceService.getSelectedDeviceId(deviceType);

  // Find the device either by stored preference or first match by type
  let device: Device | null = null;
  let staleDeviceCleared = false;
  if (workstationId && workstation?.devices) {
    if (deviceId) {
      device =
        workstation.devices.find((d: Device) => d.id === deviceId) || null;
    }
    if (!device) {
      // Stored device not found — auto-select first match by type
      device =
        workstation.devices.find((d: Device) => d.deviceType === deviceType) ||
        null;

      // If we had a stored ID that's now gone, clear the stale preference
      if (deviceId) {
        staleDeviceCleared = true;
      }
    }
  }

  // Clear stale device preference and update the corresponding service
  const clearedRef = useRef(false);
  useEffect(() => {
    if (staleDeviceCleared && !clearedRef.current) {
      clearedRef.current = true;
      workstationPreferenceService.clearDeviceForType(deviceType);
      if (device) {
        // Auto-set the fallback device as the new preference
        workstationPreferenceService.setDeviceForType(
          deviceType,
          device.id,
          device.name
        );
        if (deviceType === "slide_printer") {
          slideLabelPrinterService.setDevice(device.id, device.name);
        } else if (deviceType === "cassette_printer") {
          cassetteLabelPrinterService.setDevice(device.id, device.name);
        } else if (deviceType === "document_scanner") {
          documentScanningService.setDevice(device.id, device.name);
        }
      } else if (deviceType === "document_scanner") {
        // No fallback available — clear the service so it doesn't use stale data
        documentScanningService.clearDevice();
      }
    } else if (!staleDeviceCleared) {
      clearedRef.current = false;
    }
  }, [staleDeviceCleared, device, deviceType]);

  // Auto-heal a stale workstation preference: when the backend returns
  // 404 for the stored workstation ID, the workstation was deleted or
  // belongs to a different environment. Clear the stale preference (and
  // all device preferences, since they pointed into the dead
  // workstation's device list) and prompt the user to re-select.
  // Classification is by HTTP status code, not message string
  // (see CONTRIBUTING.md §14).
  const staleWorkstationHealedRef = useRef<string | null>(null);

  const isStaleWorkstation404 =
    !!workstationId &&
    error instanceof AxiosError &&
    error.response?.status === 404;

  const healStaleWorkstation = useCallback(() => {
    workstationPreferenceService.clearAll();
    notifications.show({
      title: "Workstation no longer available",
      message:
        "The previously selected workstation could not be found. Please re-select your workstation in the printer status panel.",
      color: "yellow",
      autoClose: 8000,
    });
  }, []);

  useEffect(() => {
    if (
      isStaleWorkstation404 &&
      workstationId &&
      staleWorkstationHealedRef.current !== workstationId
    ) {
      staleWorkstationHealedRef.current = workstationId;
      healStaleWorkstation();
    }
  }, [isStaleWorkstation404, workstationId, healStaleWorkstation]);

  if (!workstationId) {
    return {
      hasWorkstation: false,
      workstationOnline: false,
      device: null,
      deviceConnected: false,
      isLoading: false,
      workstationName: null,
    };
  }

  const deviceConnected = device?.status === "connected";

  return {
    hasWorkstation: true,
    workstationOnline: wsOnline,
    device,
    deviceConnected: wsOnline && deviceConnected,
    isLoading,
    workstationName: workstationPreferenceService.getSelectedWorkstationName(),
  };
}
